import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Object3D, ViewComponent } from '@wonderlandengine/api';
import { Easing, Tween } from '@tweenjs/tween.js';
import { quat, vec3, mat4 } from 'gl-matrix';

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const CAMERA_TARGET_POSITION = vec3.fromValues(0, 0.25, 0);
const DELTA_TIME = 0.016;
const SPEED = 2;
const SMOOTH = 5;
const DEFAULT_FOV = 48.500;

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

type Euler = {
	roll: number;  // Rotation around X-axis
	pitch: number; // Rotation around Y-axis
	yaw: number;   // Rotation around Z-axis
};

export enum CameraViews {
	ModelView,
	LeftWheelView,
	LeatherView,
	SeatsView,
	CarpetView,
	DashboardView,
	SteeringWheelView,
	LeatherDetailsView,
	HoodView,
	CowlView,
	S400GraphicsView,
	LuggageRackView,
	ExhaustView,
	AccentPackView,
	HeadrestView,
	StitchingView,
	CupHolderView,
	TunnelStorageView,
	InteriorView,
	ConsoleView,
	FinalFinishesView,
	PdfModelView,
	PdfV2ModelView,
	PdfV2WheelView1,
	PdfV2WheelView2,
	PdfV2SeatsView,
	PdfV2DashboardView,
	PdfV2SteeringWheelView,
	PdfV2GearstickView,
	PdfV2CockpitView2,
	PdfV2RearView,
	PdfV2ModelView2,
	PdfV2CowlView,
	PdfV2LuggageRackView,
	PdfV2SideView,	
	PdfV2FullView,
	PdfV2ModelView3,
	PdfV2PaintView1,
	PdfV2PaintView2,
	PdfV2RearView2
}

//-----------------------------------------------------------------------------------------
// Global Variables:
//-----------------------------------------------------------------------------------------

const tempvec3 = vec3.create();
const tempquat = quat.create();

/**
 * Camera Controller:
 *
 * Allows the user to switch between different camera views, transitioning between them smoothly as well as allowing user to rotate and zoom manually using the orbital camera.
 */
export class CameraController extends Component {
	static TypeName = 'camera-controller';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	mainCamera!: Object3D;

	@property.int(1000)
	transitionDuration = 1000;

	@property.int(200)
	orbitTransitionDuration = 200;

	@property.int(SPEED)
	xRotationSpeed = SPEED;

	@property.int(SPEED)
	tiltSpeed = SPEED;

	@property.int(SMOOTH)
	smoothTime = SMOOTH;

	@property.int(5)
	cameraTiltMin = 5;

	@property.int(80)
	cameraTiltMax = 80;

	@property.int(1.4)
	distanceSpeed = 1.4;

	@property.int(5)
	distanceMin = 5;

	@property.int(10)
	distanceMax = 10;

	@property.int(7)
	startingDistance = 7;

	@property.int(25)
	targetDistanceSpeed = 25;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	private _cameraTween: Tween<{ position: vec3; rotation: quat }> | null = null;
	private _currentView: CameraViews | null = null;
	private _cameraPositions: Record<string, vec3> = {};
	private _cameraRotations: Record<string, quat> = {};
	private _isTransitioning: boolean = false;

	private _distance: number = this.startingDistance;
	private _targetDistance: number = 10;

	private _rotationYAxis: number = 0;
	private _rotationXAxis: number = 0;

	private _velocityX: number = 0;
	private _velocityY: number = 0;

	private _orbitalCameraActive: boolean = false;
	
	private _isDragging = false;
	private _activePointers: PointerEvent[] = [];
	private _initialDistance: number | null = null;
	
	//-----------------------------------------------------------------------------------------
	// Properties:
	//-----------------------------------------------------------------------------------------

	get isTransitioning() {
		return this._isTransitioning;
	}

	get currentView() {
		return this._currentView;
	}

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {

		// iterate through child objects to populate the camera positions and camera rotations arrays.
		// the names of the children must match the enum names (spaces are ignored).
		this.object.children.forEach((child) => {

			// remove spaces from name.
			const name = child.name.replace(/\s/g, '');

			this._cameraPositions[name] = vec3.create();
			child.getPositionWorld(this._cameraPositions[name]);

			this._cameraRotations[name] = quat.create();
			child.getRotationWorld(this._cameraRotations[name]);
		});

		this._distance = this.startingDistance;
		this._targetDistance = this.startingDistance;
		this._rotationXAxis = 0;
		this._rotationYAxis = 0;
		this._orbitalCameraActive = true;
	}

	onActivate(): void {

		// subscribe to touch events.
		const canvas = this.engine.canvas;
		canvas.addEventListener('pointerdown', this._onPointerDown.bind(this));
		canvas.addEventListener('pointerup', this._onPointerUp.bind(this));
		canvas.addEventListener('pointermove', this._onPointerMove.bind(this));
		canvas.addEventListener('pointercancel', this._onPointerCancel.bind(this));
		canvas.addEventListener('wheel', this._onMouseWheel.bind(this));
	}

	onDeactivate(): void {

		// unsubscribe from touch events.
		const canvas = this.engine.canvas;
		canvas.removeEventListener('pointerdown', this._onPointerDown);
		canvas.removeEventListener('pointerup', this._onPointerUp);
		canvas.removeEventListener('pointermove', this._onPointerMove);
		canvas.removeEventListener('pointercancel', this._onPointerCancel);
		canvas.removeEventListener('wheel', this._onMouseWheel);
	}

	update(dt: number) {

		if (this._orbitalCameraActive) {
			this.mainCamera.lookAt(CAMERA_TARGET_POSITION);

			this._distance = this._moveTowards(this._distance, this._targetDistance, this.targetDistanceSpeed * dt);

			this._updateCamera = this._updateCamera.bind(this);
			requestAnimationFrame(() => this._updateCamera(this.mainCamera));
		}
		else {

			// update the camera's position and rotation on each frame if a tween is active.
			this._cameraTween?.update();
		}
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Changes the camera's view to the specified view with a smooth transition.
	 * @param newView The CameraViews enum value indicating the new camera view.
	 * @param onComplete Optional callback function, to be called upon completion of the transition.
	 * @param skipTween Optional argument to skip the tween, setting the requested view immediately instead of transitioning to it.
	 */
	changeCameraView(newView: CameraViews, onComplete?: () => void, skipTween?: boolean): void {

		// do nothing if the given view is already selected.
		if (newView === this._currentView) return;

		// update the current view.
		this._currentView = newView;

		// get the new view as a string value, so we can use it as a RecordSet key.
		const newViewAsString = CameraViews[newView];

		// disable the orbital camera.
		this._orbitalCameraActive = false;
		this._velocityX = 0;
		this._velocityY = 0;

		// if the skipTween option is specified, set the camera position and rotation straight away.
		if (skipTween) {

			// drop any transition still in flight, or update() would carry on tweening towards the old
			// target and drag the camera straight back off the viewpoint we are jumping to.
			this._cameraTween = null;
			this._isTransitioning = false;

			this.mainCamera.setPositionWorld(
				this._cameraPositions[newViewAsString]
			);
			this.mainCamera.setRotationWorld(
				this._cameraRotations[newViewAsString]
			);
			return;
		}

		// otherwise, smoothly tween both the position and rotation.
		this.mainCamera.getPositionWorld(tempvec3);
		this.mainCamera.getRotationWorld(tempquat);

		const endQuat = this._cameraRotations[newViewAsString];
		if (quat.dot(tempquat, endQuat) < 0) {
			quat.scale(endQuat, endQuat, -1); // negate to ensure the shortest path.
		}

		this._cameraTween = new Tween({
			position: tempvec3,
			rotation: tempquat,
		});

		this._cameraTween
			.to(
				{
					position: this._cameraPositions[newViewAsString],
					rotation: this._cameraRotations[newViewAsString],
				},
				this.transitionDuration
			)
			.easing(Easing.Quadratic.InOut)
			.onUpdate(this._tweenUpdate)
			.onComplete(() => {
				this._cameraTween = null;
				this._isTransitioning = false;

				// call the call back function (if provided).
				if (onComplete) {
					onComplete();
				}
			})
			.start();

		// set the flag to show we're transitioning - the above code clears the flag when the transition is complete.
		this._isTransitioning = true;
	}

	enableOrbitalCamera(): void {

		// only run this function if orbital camera isn't already enabled.
		if (!this._orbitalCameraActive) {

			// get zoom level and move to the nearest zoom within the bounds if not already.
			this._distance = this._getCameraDistance(this.mainCamera);
			this._targetDistance = Math.max(this.distanceMin, Math.min(this.distanceMax, this._distance));

			// move the camera to look at the center.
			const position = this.mainCamera.getPositionWorld(tempvec3);
			let endQuat = this._lookAt(position, CAMERA_TARGET_POSITION);
			const eulerRotation = this._quaternionToEuler(endQuat);

			this._rotationXAxis = eulerRotation.roll;
			this._rotationYAxis = eulerRotation.pitch;

			// re-calculate camera position and angle if the tilt is out of bounds.
			if (this._rotationXAxis < this.cameraTiltMin || this._rotationXAxis > this.cameraTiltMax) {
				this._rotationXAxis = this._clampAngle(this._rotationXAxis, this.cameraTiltMin, this.cameraTiltMax);

				// update Y position.
				const updatedPosition = this.mainCamera.getPositionWorld(tempvec3);
				updatedPosition[1] = 1;

				// update final rotation.
				endQuat = this._lookAt(updatedPosition, CAMERA_TARGET_POSITION);
			}

			this._velocityX = 0;
			this._velocityY = 0;

			// calculate the new position based on the target distance and rotation.
			const direction = vec3.fromValues(0, 0, -this._targetDistance);
			const rotation = quat.fromEuler(tempquat, this._rotationXAxis, this._rotationYAxis, 0);
			const newPosition = vec3.transformQuat(vec3.create(), direction, rotation);
			vec3.add(newPosition, newPosition, CAMERA_TARGET_POSITION);

			// get the current position and rotation.
			this.mainCamera.getPositionWorld(tempvec3);
			this.mainCamera.getRotationWorld(tempquat);

			// negate to ensure the shortest path.
			if (quat.dot(tempquat, endQuat) < 0) {
				quat.scale(endQuat, endQuat, -1);
			}

			this._cameraTween = new Tween({
				position: tempvec3,
				rotation: tempquat,
			});

			this._cameraTween
				.to(
					{
						position: newPosition,
						rotation: endQuat,
					},
					250
				)
				.easing(Easing.Quadratic.In)
				.onUpdate((obj) => {
					this._tweenUpdate(obj);
				})
				.onComplete(() => {

					// update distance so that the camera is correctly positioned when activated.
					this._distance = this._targetDistance;

					this._cameraTween = null;
					this._isTransitioning = false;
					this._orbitalCameraActive = true;
				})
				.start();

			this._isTransitioning = true;
		}
	}

	/**
	 * Switches the orbital camera off and reports whether it was on, so the caller can put it back.
	 * Needed around image capture: while the orbital camera is active it repositions the camera on every
	 * frame from its own distance and rotation, which overrides any viewpoint being captured.
	 */
	suspendOrbitalCamera(): boolean {
		const wasActive = this._orbitalCameraActive;
		this._orbitalCameraActive = false;
		return wasActive;
	}

	/**
	 * Re-enables the orbital camera after a capture, but only if it was active to begin with - on the
	 * summary the canvas is hidden and there is no orbit to return to.
	 */
	restoreOrbitalCamera(wasActive: boolean): void {
		if (wasActive) {
			this._orbitalCameraActive = true;
		}
	}

	updateCameraFov(fov:number) {

		// get view component in main camera (where fov is stored).
		const viewComponent = this.mainCamera.getComponent(ViewComponent);
		if (!viewComponent) {
			console.error('ViewComponent not found on main camera.');
			return;
		}
		
		// update FOV to be proportional to the change in view size (in our case 1.24 times FOV at 100vw).
		viewComponent.fov = fov;
	}

	// canvas is 124vw on desktop to position car to the left and FOV needs to adjust accordingly so the car is well sized in the scene.
	updateDesktopCameraFov() {

		// update FOV to be proportional to the change in view size (in our case 1.24 times FOV at 100vw).
		const desktopFov = DEFAULT_FOV * (124 / 100);
		this.updateCameraFov(desktopFov);
	}

	// reset fov to default (needed when taking pdf images + summary image).
	resetCameraFov() {

		this.updateCameraFov(DEFAULT_FOV);
	}

	//-----------------------------------------------------------------------------------------
	// Handlers:
	//-----------------------------------------------------------------------------------------
	
	// listen for initial mouse press.
	private _onPointerDown(event: PointerEvent) {

		const target = event.target as HTMLElement;
		if (target.hasPointerCapture(event.pointerId)) {
			target.setPointerCapture(event.pointerId);
		}

		this._activePointers.push(event);
		if (this._activePointers.length === 2) {

			// two fingers detected, calculate initial pinch distance.
			this._initialDistance = this._getDistance(this._activePointers[0], this._activePointers[1]);
		}

		this.enableOrbitalCamera();
		this._isDragging = true;
		this.engine.canvas.style.cursor = 'grabbing';
	}
	
	// listen for finish dragging.
	private _onPointerUp(event: PointerEvent) {

		this._isDragging = false;
		this.engine.canvas.style.cursor = 'grab';

		this._activePointers = this._activePointers.filter((p) => p.pointerId !== event.pointerId);

		// reset pinch zoom tracking if fewer than two fingers remain.
		if (this._activePointers.length < 2) {
			this._initialDistance = null;
		}
	}
	
	// listen for mouse move but only do anything if the user has pressed the mouse.
	private _onPointerMove(event: PointerEvent) {
		if (this._isDragging) {
			this._onMouseMove(event);

			// update the active pointer position.
			this._activePointers = this._activePointers.map((p) => (p.pointerId === event.pointerId ? event : p));

			// handle pinch zoom.
			if (this._activePointers.length === 2 && this._initialDistance !== null) {
				const newDistance = this._getDistance(this._activePointers[0], this._activePointers[1]);
				const zoomAmount = (newDistance - this._initialDistance) * 0.01;
				this._onMouseWheel({ deltaY: -zoomAmount * 100 } as WheelEvent);
				this._initialDistance = newDistance;
			}
		}
	}

	private _onMouseMove(event: PointerEvent) {
		const canvas = document.querySelector('canvas')!;
		const canvasHeight = canvas.clientHeight;
		const screenWidth = window.innerWidth;

		// normalize movementX and movementY based on screen dimensions.
		const normalizedMovementX = (event.movementX / screenWidth) * screenWidth;
		const normalizedMovementY = (event.movementY / canvasHeight) * canvasHeight;

		// calculate velocity based on the normalized distance the mouse has moved.
		this._velocityX += this.xRotationSpeed * normalizedMovementX * DELTA_TIME;
		this._velocityY += this.tiltSpeed * normalizedMovementY * DELTA_TIME;
	}
	
	// listen for pointer cancel (e.g., finger lifted unexpectedly).
	private _onPointerCancel(event: PointerEvent) {
		this._activePointers = this._activePointers.filter((p) => p.pointerId !== event.pointerId);
		if (this._activePointers.length < 2) {
			this._initialDistance = null;
		}
	}

	private _onMouseWheel(event: WheelEvent) {

		// calculate zoom based on the amount the mouse wheel has moved.
		this._targetDistance += event.deltaY * this.distanceSpeed * DELTA_TIME;
		this._targetDistance = Math.max(this.distanceMin, Math.min(this.distanceMax, this._targetDistance));
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Handles updating the camera's world position and rotation during a tween animation.
	 * @param obj An object containing the new position and rotation of the camera.
	 */
	private _tweenUpdate = (obj: { position: vec3; rotation: quat }) => {
		this.mainCamera.setPositionWorld(obj.position);
		this.mainCamera.setRotationWorld(obj.rotation);
	};

	private _updateCamera(cameraProxy: Object3D): void {

		// update() defers this by a frame, so the orbital camera may have been switched off in the meantime -
		// bail out rather than overwrite a viewpoint that has just been set.
		if (!this._orbitalCameraActive) {
			return;
		}

		this._rotationYAxis -= this._velocityX;
		this._rotationXAxis += this._velocityY;

		// contain the angle within the bounds.
		this._rotationXAxis = this._clampAngle(this._rotationXAxis, this.cameraTiltMin, this.cameraTiltMax);

		// calculate new camera position.
		const rotation = quat.fromEuler(tempquat, this._rotationXAxis, this._rotationYAxis, 0);
		const negDistance = vec3.fromValues(0, 0, -this._distance);
		const position = vec3.transformQuat(tempvec3, negDistance, rotation);
		vec3.add(position, position, CAMERA_TARGET_POSITION);

		cameraProxy.setPositionWorld(position);

		// gradually reduce the velocity.
		this._velocityX *= 1 - (DELTA_TIME * this.smoothTime);
		this._velocityY *= 1 - (DELTA_TIME * this.smoothTime);
	}

	private _getCameraDistance(cameraProxy: Object3D): number {

		// get the current position and rotation.
		const position = cameraProxy.getPositionWorld(tempvec3);
		const rotation = cameraProxy.getRotationWorld(tempquat);

		// compute offset from target position.
		const offset = vec3.subtract(tempvec3, position, CAMERA_TARGET_POSITION);

		// get the inverse rotation.
		const inverseRotation = quat.invert(tempquat, rotation);

		// apply inverse rotation to offset.
		const transformedOffset = vec3.transformQuat(tempvec3, offset, inverseRotation);

		// the Z component represents the distance.
		return transformedOffset[2];
	}

	// calculate distance between pointer events when user pinches.
	private _getDistance(p1: PointerEvent, p2: PointerEvent): number {
		return Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
	}

	private _clampAngle(angle: number, min: number, max: number): number {

		// prevent the user leaving the view of the car.
		if (angle < -360) angle += 360;
		if (angle > 360) angle -= 360;
		return Math.max(min, Math.min(max, angle));
	}

	private _moveTowards(current: number, target: number, maxDelta: number): number {

		// gradually move towards the correct zoom position, taken from Mathf.moveTowards() in Unity.
		if (Math.abs(target - current) <= maxDelta) {
			return target;
		}
		return current + Math.sign(target - current) * maxDelta;
	}

	private _lookAt(currentPosition: vec3, target: vec3): quat {

		// get the camera's current position.
		this.mainCamera.getRotationWorld(tempquat);

		// create a target-to matrix.
		const viewMatrix = mat4.create();
		mat4.targetTo(viewMatrix, currentPosition, target, [0, 1, 0]);

		// extract the rotation quaternion from the matrix.
		const targetRotation = quat.create();
		mat4.getRotation(targetRotation, viewMatrix);

		return targetRotation;
	}

	private _quaternionToEuler(q: quat): Euler {
		const x = q[0];
		const y = q[1];
		const z = q[2];
		const w = q[3];

		// roll (X-axis rotation).
		const sinrCosp = 2 * (w * x + y * z);
		const cosrCosp = 1 - 2 * (x * x + y * y);
		let roll = -Math.atan2(sinrCosp, cosrCosp) * (180 / Math.PI);

		// pitch (Y-axis rotation).
		const sinp = 2 * (w * y - z * x);
		let pitch = Math.abs(sinp) >= 1
			? -Math.sign(sinp) * 90 // Clamp to ±90 degrees
			: -Math.asin(sinp) * (180 / Math.PI);

		// yaw (Z-axis rotation).
		const sinyCosp = 2 * (w * z + x * y);
		const cosyCosp = 1 - 2 * (y * y + z * z);
		const yaw = Math.atan2(sinyCosp, cosyCosp) * (180 / Math.PI);

		if (roll > 0) {
			const dif = 90 - pitch;
			pitch = 90 + dif;
		} else if (pitch < 0 && roll < 0) {
			pitch = 360 + pitch;
		}

		if (roll < 0) roll += 180;

		return { roll, pitch, yaw };
	}
}
